import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ProjectService} from './project.service';
import {Observable, throwError} from 'rxjs';
import {catchError, map, mergeMap, shareReplay} from 'rxjs/operators';
import {MerlinxAptResponse} from '../interfaces/merlinx-apt-response';
import {MerlinxAptEntry} from '../interfaces/merlinx-apt-entry';
import {MerlinxFlightTypes} from '../models/merlinx-flight-types.enum';
import {MerlinxAptGroup} from '../interfaces/merlinx-apt-group';
import {MerlinxHotelResponse} from '../interfaces/merlinx-hotel-response';
import {MerlinxHotelEntry} from '../interfaces/merlinx-hotel-entry';
import {MerlinxCarEntry} from '../interfaces/merlinx-car-entry';
import {MerlinxCarResponse} from '../interfaces/merlinx-car-response';
import {MerlinxParkingEntry} from '../interfaces/merlinx-parking-entry';
import {MerlinxParkingResponse} from '../interfaces/merlinx-parking-response';

@Injectable({
  providedIn: 'root'
})
export class MerlinxService {

  constructor(private http: HttpClient,
              private ps: ProjectService) {
  }

  httpCache: Map<string, Observable<any>> = new Map<string, Observable<any>>();

  airportQuery(query: string): Observable<MerlinxAptEntry[]> {
    return this.ps.getMerlinxCode().pipe(
      mergeMap(e => {
        const url = `https://welcometravel.vcms.eu/agency/${e}/index/andata_e_ritorno/dyndepartureAjax/?q=${query}`;
        if (!this.httpCache.get(url)) {
          this.httpCache.set(url, this.http.get<MerlinxAptResponse>(url).pipe(shareReplay({bufferSize: 1, refCount: true})));
        }
        return this.httpCache.get(url);
      }),
      map(e => {
        const array = Object.values(e).filter((x: MerlinxAptEntry | MerlinxAptGroup) => x.type === MerlinxFlightTypes.AIRPORT) as MerlinxAptEntry[];
        const aptGroups = Object.values(e).filter((x: MerlinxAptEntry | MerlinxAptGroup) => x.type === MerlinxFlightTypes.AIRPORT_GROUP) as MerlinxAptGroup[];
        return [...array, ...aptGroups.flatMap(x => Object.values(x.group)) ];
      }),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }

  hotelQuery(query: string): Observable<MerlinxHotelEntry[]> {
    return this.ps.getMerlinxCode().pipe(
      mergeMap(e => {
        const url = `https://welcometravel.vcms.eu/agency/${e}/index/hotel/dynhtllocationAjax/?q=${query}`;
        if (!this.httpCache.get(url)) {
          this.httpCache.set(url, this.http.get<MerlinxHotelResponse>(url).pipe(shareReplay({bufferSize: 1, refCount: true})));
        }
        return this.httpCache.get(url);
      }),
      map(e => [...e?.c, ...e?.h].filter(x => x)),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }

  carQuery(query: string): Observable<MerlinxCarEntry[]> {
    return this.ps.getMerlinxCode().pipe(
      mergeMap(e => {
        const url = `https://welcometravel.vcms.eu/agency/${e}/index/car/cardyndeplocationAjax/?q=${query}`;
        if (!this.httpCache.get(url)) {
          this.httpCache.set(url, this.http.get<MerlinxCarResponse>(url).pipe(shareReplay({bufferSize: 1, refCount: true})));
        }
        return this.httpCache.get(url);
      }),
      map(e => Object.values(e).filter(x => x) as MerlinxCarEntry[]),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }

  parkingQuery(query: string): Observable<MerlinxParkingEntry[]> {
    return this.ps.getMerlinxCode().pipe(
      mergeMap(e => {
        const url = `https://welcometravel.vcms.eu/agency/${e}/index/parcheggio/deppAjax`;
        if (!this.httpCache.get(url)) {
          this.httpCache.set(url, this.http.get<MerlinxParkingResponse>(url).pipe(shareReplay({bufferSize: 1, refCount: true})));
        }
        return this.httpCache.get(url);
      }),
      map(e => query ? e?.prk?.filter(x => x.code.toLowerCase().includes(query.toLowerCase()) || x.name.toLowerCase().includes(query.toLowerCase())) : e?.prk),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }
}
